import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  getLoyaltyConfig,
  updateLoyaltyConfig,
} from "../../../redux/loyaltyPoints/action";

const validationSchema = Yup.object({
  earning_rate_points: Yup.number()
    .required("Required")
    .positive("Must be greater than 0")
    .integer("Must be a whole number"),
  earning_rate_sar: Yup.number()
    .required("Required")
    .positive("Must be greater than 0"),
  conversion_rate_points: Yup.number()
    .required("Required")
    .positive("Must be greater than 0")
    .integer("Must be a whole number"),
  conversion_rate_sar: Yup.number()
    .required("Required")
    .positive("Must be greater than 0"),
  status: Yup.number()
    .required("Required")
    .oneOf([0, 1], "Must be Active or Inactive"),
  min_order_amount: Yup.number()
    .required("Required")
    .min(0, "Cannot be negative"),
  min_redemption_points: Yup.number()
    .required("Required")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number"),
  review_reward_points: Yup.number()
    .required("Required")
    .min(0, "Cannot be negative")
    .integer("Must be a whole number"),
});

const LoyaltyConfig = () => {
  const dispatch = useDispatch();
  const config = useSelector((state) => state.reusableSlice.LoyaltyConfig);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(getLoyaltyConfig());
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      earning_rate_points: config?.earning_rate_points || "",
      earning_rate_sar: config?.earning_rate_sar || "",
      conversion_rate_points: config?.conversion_rate_points || "",
      conversion_rate_sar: config?.conversion_rate_sar || "",
      status: config?.status ?? 1,
      min_order_amount: config?.min_order_amount ?? "",
      min_redemption_points: config?.min_redemption_points || "",
      review_reward_points: config?.review_reward_points ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      dispatch(updateLoyaltyConfig(values, setLoading));
    },
  });

  return (
    <div className="loyalty-config-form">
      {/* Summary card showing current settings */}
      {config && (
        <div className="loyalty-rate-summary">
          <p>
            <strong>How customers earn:</strong> Every {config.earning_rate_sar}{" "}
            SAR spent = {config.earning_rate_points} point(s)
          </p>
          <p>
            <strong>How points convert:</strong> {config.conversion_rate_points}{" "}
            points = {config.conversion_rate_sar} SAR in wallet
          </p>
          <p>
            <strong>Review Reward:</strong>{" "}
            {config.review_reward_points > 0
              ? `${config.review_reward_points} point(s) per review`
              : "Disabled"}
          </p>
          <p>
            <strong>Program Status:</strong>{" "}
            <span
              className={`badge ${config.status === 1 ? "bg-success" : "bg-secondary"}`}
            >
              {config.status === 1 ? "Active" : "Inactive"}
            </span>
          </p>
        </div>
      )}

      <form onSubmit={formik.handleSubmit}>
        {/* Earning Menu */}
        <h5 className="mb-3">Points Earning Menu</h5>

        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Points to Give per Riyal</label>
              <input
                type="number"
                name="earning_rate_points"
                placeholder="e.g. 1"
                className={`form-control ${formik.touched.earning_rate_points && formik.errors.earning_rate_points ? "is-invalid" : ""}`}
                value={formik.values.earning_rate_points}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.earning_rate_points &&
                formik.errors.earning_rate_points && (
                  <div className="invalid-feedback">
                    {formik.errors.earning_rate_points}
                  </div>
                )}
              <small className="text-muted">
                Number of points the customer receives
              </small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>For Every (SAR)</label>
              <input
                type="number"
                step="0.01"
                name="earning_rate_sar"
                placeholder="e.g. 1.00"
                className={`form-control ${formik.touched.earning_rate_sar && formik.errors.earning_rate_sar ? "is-invalid" : ""}`}
                value={formik.values.earning_rate_sar}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.earning_rate_sar &&
                formik.errors.earning_rate_sar && (
                  <div className="invalid-feedback">
                    {formik.errors.earning_rate_sar}
                  </div>
                )}
              <small className="text-muted">
                SAR amount spent to earn those points
              </small>
            </div>
          </div>
        </div>

        {/* Review Reward */}
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Points for Writing a Review</label>
              <input
                type="number"
                name="review_reward_points"
                placeholder="e.g. 10"
                className={`form-control ${formik.touched.review_reward_points && formik.errors.review_reward_points ? "is-invalid" : ""}`}
                value={formik.values.review_reward_points}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.review_reward_points &&
                formik.errors.review_reward_points && (
                  <div className="invalid-feedback">
                    {formik.errors.review_reward_points}
                  </div>
                )}
              <small className="text-muted">
                Points given when a customer reviews a product (0 = disabled)
              </small>
            </div>
          </div>
        </div>

        {/* Conversion Settings */}
        <h5 className="mb-3 mt-4">Conversion Menu calculator</h5>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Points Needed</label>
              <input
                type="number"
                name="conversion_rate_points"
                placeholder="e.g. 100"
                className={`form-control ${formik.touched.conversion_rate_points && formik.errors.conversion_rate_points ? "is-invalid" : ""}`}
                value={formik.values.conversion_rate_points}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.conversion_rate_points &&
                formik.errors.conversion_rate_points && (
                  <div className="invalid-feedback">
                    {formik.errors.conversion_rate_points}
                  </div>
                )}
              <small className="text-muted">How many points to exchange</small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Equal to Riyal</label>
              <input
                type="number"
                step="0.01"
                name="conversion_rate_sar"
                placeholder="e.g. 1.00"
                className={`form-control ${formik.touched.conversion_rate_sar && formik.errors.conversion_rate_sar ? "is-invalid" : ""}`}
                value={formik.values.conversion_rate_sar}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.conversion_rate_sar &&
                formik.errors.conversion_rate_sar && (
                  <div className="invalid-feedback">
                    {formik.errors.conversion_rate_sar}
                  </div>
                )}
              <small className="text-muted">
                SAR added to wallet for those points
              </small>
            </div>
          </div>
        </div>

        {/* Rules */}
        <h5 className="mb-3 mt-4">Rules</h5>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label>Minimum Order Amount (SAR)</label>
              <input
                type="number"
                step="0.01"
                name="min_order_amount"
                placeholder="e.g. 0.00"
                className={`form-control ${formik.touched.min_order_amount && formik.errors.min_order_amount ? "is-invalid" : ""}`}
                value={formik.values.min_order_amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.min_order_amount &&
                formik.errors.min_order_amount && (
                  <div className="invalid-feedback">
                    {formik.errors.min_order_amount}
                  </div>
                )}
              <small className="text-muted">
                Customer must spend at least this amount to earn points (0 = all
                orders qualify)
              </small>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group">
              <label>Minimum Points to Convert</label>
              <input
                type="number"
                name="min_redemption_points"
                placeholder="e.g. 100"
                className={`form-control ${formik.touched.min_redemption_points && formik.errors.min_redemption_points ? "is-invalid" : ""}`}
                value={formik.values.min_redemption_points}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.min_redemption_points &&
                formik.errors.min_redemption_points && (
                  <div className="invalid-feedback">
                    {formik.errors.min_redemption_points}
                  </div>
                )}
              <small className="text-muted">
                Customer needs at least this many points before they can convert
                to SAR
              </small>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="row mt-3">
          <div className="col-md-6">
            <div className="form-group">
              <label>Program Status</label>
              <select
                name="status"
                className={`form-control ${formik.touched.status && formik.errors.status ? "is-invalid" : ""}`}
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
              {formik.touched.status && formik.errors.status && (
                <div className="invalid-feedback">{formik.errors.status}</div>
              )}
              <small className="text-muted">
                Turn the loyalty program on or off for all customers
              </small>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary mt-4"
          disabled={loading}
        >
          {loading ? "Saving..." : "Save Configuration"}
        </button>
      </form>
    </div>
  );
};

export default LoyaltyConfig;
